import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Building2, Target, TrendingUp, Calendar, Loader2, Briefcase, DollarSign, BarChart3, ChevronLeft, ChevronRight } from "lucide-react";
import { AreaChart, Area, Tooltip, ResponsiveContainer, XAxis, YAxis } from "recharts";
import api from '../api/axios';
import { useAuth } from '../auth/AuthContext';
import AddContactModal from "../components/AddContactModal";
import AddDealModal from "../components/AddDealModal";
import AddCompanyModal from "../components/AddCompanyModal";
import { useToast } from "../components/ui/ToastContext";
import {
  STAGE_PROBABILITY,
  getWeekRange,
  getMonthRange,
  filterDealsByPeriod,
  calculateWeightedRevenue,
  buildPipelineData,
  calculateQualifiedPipeline,
  calculateTotalPipelineValue,
} from '../utils/dashboardUtils';

// HubSpot-style KPI Card with colored accent border
function KpiCard({ label, value, valuecolor, icon, subtitle, chart, onClick }) {
  return (
    <div 
      onClick={onClick}
      style={{
        flex: 1,
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
        padding: "1.25rem",
        borderLeft: `4px solid ${valuecolor || '#e2e8f0'}`,
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "110px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: "0.9rem", color: "#64748b", fontWeight: 500, marginBottom: "0.4rem" }}>{label}</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: valuecolor || "#1e293b" }}>{value}</div>
          {subtitle && <div style={{ fontSize: "0.8rem", color: "#94a3b8", marginTop: "0.2rem" }}>{subtitle}</div>}
        </div>
        <div style={{
          background: `${valuecolor}15`,
          borderRadius: "8px",
          padding: "0.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {icon}
        </div>
      </div>
      {chart && <div style={{ marginTop: "0.75rem" }}>{chart}</div>}
    </div>
  );
}

// Open Outlook meeting helper
const openOutlookMeeting = ({ subject, body, attendees = [] }) => {
  const params = new URLSearchParams({
    subject,
    body,
    to: attendees.join(";"),
  });
  const url = `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
  window.open(url, "_blank");
};

const openEmailCompose = ({ subject, body, to = "" }) => {
  const params = new URLSearchParams({ subject, body });
  window.location.href = `mailto:${to}?${params.toString()}`;
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  const [deals, setDeals] = useState([]);
  const [stages, setStages] = useState([]);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [pipelinePeriod, setPipelinePeriod] = useState("all");
  const [pipelineOffset, setPipelineOffset] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const performerPeriod = "month";
  const pickerRef = useRef(null);
  const { user } = useAuth();
  const toast = useToast();
  const [dashboardError, setDashboardError] = useState(false);

  // Fetch dashboard metrics
  const fetchDashboard = async (period = "month") => {
    try {
      const res = await api.get(`/api/dashboard-metrics/?period=${period}`);
      setDashboard(res.data);
      setDashboardError(false);
    } catch (err) {
      console.error(err);
      setDashboardError(true);
    }
  };

  // Fetch deals
  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const res = await api.get("/api/deals/summary");
        setDeals(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setDealsLoading(false);
      }
    };
    fetchDeals();
  }, []);

  useEffect(() => {
    const loadStages = async () => {
      try {
        const res = await api.get('/api/stages/');
        setStages(res.data || []);
      } catch (err) {
        console.error('Failed to load stages', err);
      }
    };
    loadStages();
  }, []);

  // Refetch when performer period changes
  useEffect(() => {
    fetchDashboard(performerPeriod).finally(() => setLoading(false));
  }, [performerPeriod]);

  const openAddModal = () => setShowAddModal(true);
  const closeAddModal = () => setShowAddModal(false);

  const handleContactSaved = () => {
    closeAddModal();
    fetchDashboard(performerPeriod);
    toast.success("Contact added successfully.");
  };

  const openAddDealModal = () => setShowAddDealModal(true);
  const closeAddDealModal = () => setShowAddDealModal(false);

  const handleDealSaved = () => {
    closeAddDealModal();
    fetchDashboard(performerPeriod);
    toast.success("Deal added successfully.");
    setDealsLoading(true);
    const fetchDeals = async () => {
      try {
        const res = await api.get("/api/deals/summary");
        setDeals(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setDealsLoading(false);
      }
    };
    fetchDeals();
  };

  const openAddCompanyModal = () => {
    setShowAddCompanyModal(true);
  };

  const closeAddCompanyModal = () => {
    setShowAddCompanyModal(false);
  };

  const handleCompanySaved = () => {
    closeAddCompanyModal();
    fetchDashboard(performerPeriod);
    toast.success("Company added successfully.");
  };

  if (loading || dealsLoading) return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      background: "#f8fafc",
      gap: "1rem"
    }}>
      <Loader2 size={48} color="#3b82f6" style={{
        animation: "spin 1s linear infinite"
      }} />
      <div style={{ fontSize: "1.1rem", color: "#64748b", fontWeight: 500 }}>Loading Dashboard...</div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
  if (!dashboard) return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      gap: "1rem",
      textAlign: "center",
      padding: "2rem",
    }}>
      <BarChart3 size={48} color="#cbd5e1" />
      <div style={{ fontSize: "1.1rem", fontWeight: 600, color: "#334155" }}>
        {dashboardError ? "We couldn't load your dashboard" : "No dashboard data yet"}
      </div>
      <div style={{ fontSize: "0.9rem", color: "#64748b", maxWidth: 360 }}>
        {dashboardError
          ? "Something went wrong while fetching your metrics. Please try again."
          : "Once you add companies, contacts, and deals, your metrics will show up here."}
      </div>
      {dashboardError && (
        <button
          type="button"
          onClick={() => fetchDashboard(performerPeriod)}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
        >
          Retry
        </button>
      )}
    </div>
  );

  // Active deals
  const activeDeals = deals.filter(d => !["closed_won", "closed_lost"].includes(d.stage));

  // KPI charts (deals & revenue)
  const now = new Date();
  const currentYear = now.getFullYear();
  const months = Array.from({ length: 12 }, (_, i) => ({
    month: new Date(currentYear, i).toLocaleString('default', { month: 'short' }),
    deals: 0
  }));

  activeDeals.forEach(deal => {
    const d = new Date(deal.created_at);
    if (d.getFullYear() === currentYear) months[d.getMonth()].deals += 1;
  });

  const activeDealsChart = (
    <div style={{ width: "100%", height: 60 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={months}>
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
          <YAxis hide />
          <Tooltip formatter={(value) => [`${value} deal${value > 1 ? "s" : ""}`, "Active"]} cursor={{ fill: "rgba(0,0,0,0.05)" }} />
          <Area type="monotone" dataKey="deals" stroke="#a78bfa" fill="#e9d5ff" strokeWidth={2} dot={{ r: 3 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );

  const revenueByMonth = Array.from({ length: 12 }, (_, i) => ({
    month: new Date(currentYear, i).toLocaleString('default', { month: 'short' }),
    revenue: 0
  }));

  activeDeals.forEach(deal => {
    const d = new Date(deal.created_at);
    if (d.getFullYear() === currentYear) {
      let amount = 0;
      if (typeof deal.amount === "string") amount = Number(deal.amount.replace(/[$,]/g, '')) || 0;
      else if (typeof deal.amount === "number") amount = deal.amount;
      revenueByMonth[d.getMonth()].revenue += amount;
    }
  });

  const revenueChart = (
    <div style={{ width: "100%", height: 60 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={revenueByMonth}>
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
          <YAxis hide />
          <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, "Revenue"]} cursor={{ fill: "rgba(0,0,0,0.05)" }} />
          <Area type="monotone" dataKey="revenue" stroke="#fb923c" fill="#fed7aa" strokeWidth={2} dot={{ r: 3 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );

  // KPI cards - Row 1: Core metrics with charts
  const kpiRow1 = [
    { label: "Total Contacts", value: dashboard.total_contacts, valuecolor: "#3b82f6", icon: <Users size={24} color="#3b82f6" />, onClick: () => navigate("/customers") },
    { label: "Companies", value: dashboard.total_companies, valuecolor: "#22c55e", icon: <Building2 size={24} color="#22c55e" />, onClick: () => navigate("/companies") },
    { label: "Active Deals", value: activeDeals.length, valuecolor: "#a78bfa", icon: <Target size={24} color="#a78bfa" />, chart: activeDealsChart, onClick: () => navigate("/deals") },
  ];

  // Quick Actions
  const quickActionsPrimary = [
    // Hide add actions for basic 'user' role
    ...(user?.role === 'user' ? [] : [
      { label: "Add Contact", icon: <Users size={24} />, color: "#3b82f6" },
      { label: "Add Deal", icon: <Briefcase size={24} />, color: "#22c55e" },
      { label: "Add Company", icon: <Building2 size={24} />, color: "#10b981" }
    ])
  ];

  const quickActionsSecondary = [
    { label: "Schedule Meeting", icon: <Calendar size={24} />, color: "#a78bfa" },
    { label: "Create Task", icon: <Target size={24} />, color: "#f59e0b" },
    { label: "Send Email", icon: <Users size={24} />, color: "#6366f1" }
  ];

  // Filter deals by selected pipeline period with offset
  const getFilteredDeals = () => filterDealsByPeriod(deals, pipelinePeriod, pipelinePeriod === "all" ? 0 : pipelineOffset);

  const getPeriodLabel = () => {
    const today = new Date();
    if (pipelinePeriod === "weekly") {
      const dayOfWeek = today.getDay();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      startOfWeek.setHours(0, 0, 0, 0);
      startOfWeek.setDate(startOfWeek.getDate() + pipelineOffset * 7);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const suffix = pipelineOffset === 0 ? ' (This Week)' : pipelineOffset === -1 ? ' (Last Week)' : '';
      return `${fmt(startOfWeek)} – ${fmt(endOfWeek)}${suffix}`;
    }
    if (pipelinePeriod === "monthly") {
      const start = new Date(today.getFullYear(), today.getMonth() + pipelineOffset, 1);
      const label = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const suffix = pipelineOffset === 0 ? ' (This Month)' : pipelineOffset === -1 ? ' (Last Month)' : '';
      return `${label}${suffix}`;
    }
    return '';
  };

  const filteredDeals = getFilteredDeals();

  // Build pipeline data from stages and filtered deals
  const pipelineData = buildPipelineData(stages, filteredDeals, STAGE_PROBABILITY);

  // Maximum deals for progress bar
  const maxDeals = Math.max(...(pipelineData.map(p => p.deals) || [1]), 1);

  // Calculate metrics using utility functions
  const totalPipelineValue = calculateTotalPipelineValue(filteredDeals, ["closed_lost"]);
  const totalProjectedRevenue = calculateWeightedRevenue(filteredDeals, STAGE_PROBABILITY, ["closed_lost"]);
  const qualifiedPipeline = calculateQualifiedPipeline(pipelineData);

  const kpiRow2 = [
    { label: "Pipeline Value", value: `$${totalPipelineValue.toLocaleString()}`, valuecolor: "#fb923c", icon: <TrendingUp size={24} color="#fb923c" />, chart: revenueChart, onClick: () => navigate("/deals") },
    { label: "Projected Revenue", value: `$${Math.round(totalProjectedRevenue).toLocaleString()}`, valuecolor: "#10b981", icon: <DollarSign size={24} color="#10b981" />, subtitle: "weighted stages (excludes closed lost)" },
    { label: "Qualified Pipeline", value: `$${Math.round(qualifiedPipeline).toLocaleString()}`, valuecolor: "#eab308", icon: <BarChart3 size={24} color="#eab308" />, subtitle: "weighted stages (excludes closed won/lost)" },
  ];


  return (
    <div style={{ padding: "2rem", background: "#f8fafc", minHeight: "100vh" }}>
      {/* KPI Cards - Row 1 */}
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        {kpiRow1.map((card, idx) => <KpiCard key={idx} {...card} />)}
      </div>
      {/* KPI Cards - Row 2 */}
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem" }}>
        {kpiRow2.map((card, idx) => <KpiCard key={`r2-${idx}`} {...card} />)}
      </div>

      {/* Quick Actions & Top Performers */}
      <div style={{ display: "flex", gap: "2rem", marginBottom: "2rem", alignItems: "stretch" }}>
        <div style={{ flex: 2, background: "#fff", borderRadius: "16px", padding: "1.5rem" }}>
          <div style={{ fontWeight: 700, fontSize: "1.2rem", marginBottom: "1.5rem" }}>Quick Actions</div>

          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div style={{ display: "flex", gap: "1.5rem" }}>
              {quickActionsPrimary.map((action, idx) => (
                <button key={idx} onClick={() => {
                  if (action.label === "Add Contact") openAddModal();
                  if (action.label === "Add Deal") openAddDealModal();
                  if (action.label === "Add Company") openAddCompanyModal();
                }} style={{
                  flex: 1, background: "#f1f5f9", border: "none", borderRadius: "12px",
                  padding: "1.2rem", fontWeight: 600, fontSize: "1rem", color: action.color,
                  display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer"
                }}>
                  {action.icon}{action.label}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: "1.5rem" }}>
              {quickActionsSecondary.map((action, idx) => (
                <button key={idx} onClick={() => {
                  if (action.label === "Schedule Meeting") openOutlookMeeting({ subject: "CRM Meeting", body: "Discussion related to deals and next steps.", attendees: [] });
                  if (action.label === "Create Task/Reminder") window.open("https://to-do.live.com/tasks", "_blank");
                  if (action.label === "Send Email") openEmailCompose({ subject: "CRM Follow-up", body: "Hello,\n\n" });
                }} style={{
                  flex: 1, background: "#f1f5f9", border: "none", borderRadius: "12px",
                  padding: "1.2rem", fontWeight: 600, fontSize: "1rem", color: action.color,
                  display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer"
                }}>
                  {action.icon}{action.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Top Performers & Upcoming Tasks */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "2rem" }}>
          <div style={{ background: "#fff", borderRadius: "16px", padding: "1.5rem" }}>
            <div style={{ fontWeight: 700, fontSize: "1.2rem", marginBottom: "0.25rem" }}>Top Performers</div>
            <div style={{ fontSize: "0.8rem", color: "#94a3b8", marginBottom: "1rem" }}>Ranked by closed-won revenue</div>
            {(() => {
              const performers = (dashboard.top_performers || []).slice(0, 3);
              const maxRevenue = performers.length > 0 ? performers[0].total_revenue : 1;
              const medalColors = ["#fde68a", "#e0e7ff", "#fbcfe8"];
              const medalTextColors = ["#92400e", "#3730a3", "#9d174d"];
              return performers.length > 0 ? performers.map((p, idx) => (
                <div key={p.user_id || idx} style={{ marginBottom: "0.85rem" }}>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "0.3rem" }}>
                    <div style={{
                      width: "28px", height: "28px", borderRadius: "50%",
                      background: medalColors[idx] || "#e2e8f0",
                      color: medalTextColors[idx] || "#64748b",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, fontSize: "0.9rem", marginRight: "0.75rem", flexShrink: 0
                    }}>{idx + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.95rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.full_name || p.username}</div>
                    </div>
                    <div style={{ fontWeight: 700, color: "#10b981", fontSize: "0.9rem", whiteSpace: "nowrap" }}>${(p.total_revenue || 0).toLocaleString()}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", marginLeft: "2.5rem" }}>
                    <div style={{ height: "6px", flex: 1, background: "#e2e8f0", borderRadius: "3px", marginRight: "0.5rem" }}>
                      <div style={{ width: `${(p.total_revenue / maxRevenue) * 100}%`, height: "100%", background: "#10b981", borderRadius: "3px" }} />
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", whiteSpace: "nowrap" }}>{p.deals_closed} deal{p.deals_closed !== 1 ? "s" : ""} won</div>
                  </div>
                </div>
              )) : <div style={{ color: "#64748b", fontSize: "0.9rem" }}>No closed-won deals yet</div>;
            })()}
          </div>
        </div>
      </div>

      {/* Deal Pipeline Overview */}
      <div style={{ background: "#fff", borderRadius: "16px", padding: "2rem", marginBottom: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <div style={{ fontWeight: 700, fontSize: "1.2rem" }}>Deal Pipeline Overview</div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            {pipelinePeriod !== "all" && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", position: "relative" }} ref={pickerRef}>
                <button
                  onClick={() => setPipelineOffset(o => o - 1)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 6, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer" }}
                ><ChevronLeft size={14} color="#475569" /></button>
                <span
                  onClick={() => setPickerOpen(o => !o)}
                  style={{ fontSize: "0.82rem", fontWeight: 600, color: "#334155", minWidth: "140px", textAlign: "center", cursor: "pointer", padding: "4px 8px", borderRadius: 6, background: pickerOpen ? "#eff6ff" : "transparent", transition: "background 0.15s" }}
                >{getPeriodLabel()}</span>
                <button
                  onClick={() => setPipelineOffset(o => o < 0 ? o + 1 : o)}
                  disabled={pipelineOffset >= 0}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 6, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: pipelineOffset >= 0 ? "not-allowed" : "pointer", opacity: pipelineOffset >= 0 ? 0.4 : 1 }}
                ><ChevronRight size={14} color="#475569" /></button>

                {pickerOpen && (() => {
                  const today = new Date();
                  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const initYear = () => {
                    const { start } = pipelinePeriod === 'monthly' ? getMonthRange(pipelineOffset) : getWeekRange(pipelineOffset);
                    return start.getFullYear();
                  };
                  const PickerDropdown = () => {
                    const [year, setYear] = useState(initYear);
                    const [monthForWeeks, setMonthForWeeks] = useState(null);
                    const isFutureMonth = (y, m) => y > today.getFullYear() || (y === today.getFullYear() && m > today.getMonth());
                    const getMonthOff = (y, m) => (y - today.getFullYear()) * 12 + (m - today.getMonth());

                    const containerStyle = { position: 'absolute', top: '100%', right: 0, marginTop: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', zIndex: 100, minWidth: 244 };
                    const navBtnStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', color: '#475569', flexShrink: 0 };

                    if (pipelinePeriod === 'weekly' && monthForWeeks !== null) {
                      const firstDay = new Date(year, monthForWeeks, 1);
                      const lastDay = new Date(year, monthForWeeks + 1, 0);
                      const weeks = [];
                      const getWeekStart = (date) => { const d = new Date(date); const day = d.getDay(); d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); d.setHours(0,0,0,0); return d; };
                      let current = getWeekStart(firstDay);
                      while (current <= lastDay) { const end = new Date(current); end.setDate(current.getDate() + 6); end.setHours(23,59,59,999); weeks.push({ start: new Date(current), end }); current = new Date(current); current.setDate(current.getDate() + 7); }
                      const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      const currentWeekStart = getWeekStart(new Date());
                      const getWeekOff = (ws) => Math.round((ws - currentWeekStart) / (7*24*60*60*1000));

                      return (
                        <div style={containerStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 6 }}>
                            <button style={navBtnStyle} onClick={() => setMonthForWeeks(null)}><ChevronLeft size={13} /></button>
                            <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>{MONTHS[monthForWeeks]} {year}</span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {weeks.map((week, i) => {
                              const isFuture = week.start > currentWeekStart;
                              const isActive = getWeekOff(week.start) === pipelineOffset;
                              return (
                                <button key={i} disabled={isFuture} onClick={() => { if (!isFuture) { setPipelineOffset(getWeekOff(week.start)); setPickerOpen(false); } }}
                                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 7, border: 'none', background: isActive ? '#3b82f6' : isFuture ? 'transparent' : '#f8fafc', color: isActive ? '#fff' : isFuture ? '#cbd5e1' : '#334155', cursor: isFuture ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: isActive ? 700 : 500 }}
                                  onMouseEnter={e => { if (!isFuture && !isActive) e.currentTarget.style.background = '#eff6ff'; }}
                                  onMouseLeave={e => { if (!isFuture && !isActive) e.currentTarget.style.background = '#f8fafc'; }}
                                >
                                  <span style={{ color: isActive ? '#bfdbfe' : '#94a3b8', fontSize: '0.72rem', minWidth: 22 }}>W{i+1}</span>
                                  <span>{fmt(week.start)} – {fmt(week.end)}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div style={containerStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                          <button style={navBtnStyle} onClick={() => setYear(y => y - 1)}><ChevronLeft size={13} /></button>
                          <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{year}</span>
                          <button style={{ ...navBtnStyle, opacity: year >= today.getFullYear() ? 0.35 : 1, cursor: year >= today.getFullYear() ? 'not-allowed' : 'pointer' }} disabled={year >= today.getFullYear()} onClick={() => setYear(y => y + 1)}><ChevronRight size={13} /></button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                          {MONTHS.map((name, i) => {
                            const future = isFutureMonth(year, i);
                            const isActive = pipelinePeriod === 'monthly' && getMonthOff(year, i) === pipelineOffset;
                            return (
                              <button key={i} disabled={future} onClick={() => { if (future) return; if (pipelinePeriod === 'monthly') { setPipelineOffset(getMonthOff(year, i)); setPickerOpen(false); } else setMonthForWeeks(i); }}
                                style={{ padding: '8px 4px', borderRadius: 7, border: 'none', background: isActive ? '#3b82f6' : future ? 'transparent' : '#f8fafc', color: isActive ? '#fff' : future ? '#cbd5e1' : '#334155', cursor: future ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: isActive ? 700 : 500, transition: 'background 0.15s' }}
                                onMouseEnter={e => { if (!future && !isActive) e.currentTarget.style.background = '#eff6ff'; }}
                                onMouseLeave={e => { if (!future && !isActive) e.currentTarget.style.background = '#f8fafc'; }}
                              >{name}</button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  };
                  return <PickerDropdown />;
                })()}
              </div>
            )}
            <div style={{ display: "flex", background: "#f1f5f9", borderRadius: "8px", padding: "3px" }}>
              <button
                onClick={() => { setPipelinePeriod("all"); setPipelineOffset(0); }}
                style={{
                  padding: "0.4rem 1rem",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  background: pipelinePeriod === "all" ? "#3b82f6" : "transparent",
                  color: pipelinePeriod === "all" ? "#fff" : "#64748b",
                  transition: "all 0.2s ease"
                }}
              >All</button>
              <button
                onClick={() => { setPipelinePeriod("weekly"); setPipelineOffset(0); }}
                style={{
                  padding: "0.4rem 1rem",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  background: pipelinePeriod === "weekly" ? "#3b82f6" : "transparent",
                  color: pipelinePeriod === "weekly" ? "#fff" : "#64748b",
                  transition: "all 0.2s ease"
                }}
              >Weekly</button>
              <button
                onClick={() => { setPipelinePeriod("monthly"); setPipelineOffset(0); }}
                style={{
                  padding: "0.4rem 1rem",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  background: pipelinePeriod === "monthly" ? "#3b82f6" : "transparent",
                  color: pipelinePeriod === "monthly" ? "#fff" : "#64748b",
                  transition: "all 0.2s ease"
                }}
              >Monthly</button>
            </div>
          </div>
        </div>
        {pipelineData.map((stage, idx) => (
          <div key={idx} style={{ marginBottom: "1rem", padding: "0.85rem 1rem", background: "#f8fafc", borderRadius: "10px", border: "1px solid #f1f5f9" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
              <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: stage.color, marginRight: "0.75rem", flexShrink: 0 }} />
              <div style={{ flex: 1, fontWeight: 600, fontSize: "0.9rem", color: "#1e293b" }}>{stage.stage} <span style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 400 }}>({stage.probability}%)</span></div>
              <div style={{ color: "#475569", fontSize: "0.85rem", fontWeight: 600 }}>{stage.deals} deal{stage.deals !== 1 ? "s" : ""}</div>
              <div style={{ marginLeft: "1.5rem", fontSize: "0.85rem", fontWeight: 700, color: "#1e293b" }}>${stage.amount.toLocaleString()}</div>
              <div style={{ marginLeft: "1.5rem", fontSize: "0.85rem", fontWeight: 700, color: "#22c55e" }}>${Math.round(stage.projectedRevenue).toLocaleString()}</div>
            </div>
            <div style={{ height: "5px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
              <div style={{
                width: `${(stage.deals / maxDeals) * 100}%`,
                height: "100%",
                background: `linear-gradient(90deg, ${stage.color}, ${stage.color}cc)`,
                borderRadius: "3px",
                transition: "width 0.3s ease"
              }} />
            </div>
          </div>
        ))}
        {pipelineData.length === 0 && <div style={{ color: "#64748b" }}>No pipeline data</div>}
      </div>

      {/* Add Contact & Deal Modals */}
      {showAddModal && <AddContactModal onClose={closeAddModal} onSuccess={handleContactSaved} />}
      {showAddDealModal && <AddDealModal onClose={closeAddDealModal} onSuccess={handleDealSaved} />}
      {showAddCompanyModal && (
        <AddCompanyModal onClose={closeAddCompanyModal} onSuccess={handleCompanySaved} />
      )}
    </div>
  );
}
